<template>
  <div class="text-left">
    <v-card>
      <v-form class="pa-6" ref="form">
        <!-- Button to open the SearchScopusForm modal -->
        <v-row justify="end">
            <v-btn color="primary" @click="$refs.searchScopusForm.openDialog()">
            <v-icon left>mdi-auto-fix</v-icon>
            {{  $t("form.search_scopus") }}
          </v-btn>
        </v-row>

        <!-- Include the SearchScopusForm component -->
        <SearchScopusForm ref="searchScopusForm" @use-data="handleScopusData" />

        <v-row>
          <v-col cols="12">
            <h3>{{ $t("title.publicationData") }}</h3>
          </v-col>
        </v-row>

        <h5 class="mb-2">
          {{ $t("form.title") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0 mt-2">
            <v-text-field
              v-model="publication.title"
              :label="''"
              :placeholder="$t('form.title')"
              required
              outlined
              :rules="rules.char_required_max_250"
              :counter="250"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4" class="py-0">
            <h5 class="mb-2">
              {{ $t('form.publication.language') }}
            </h5>
            <v-select
              v-model="publication.language"
              :items="languageOptions"
              item-text="text"
              item-value="value"
              :label="''"
              :placeholder="$t('form.publication.language')"
              :rules="rules.fieldRequired"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h5 class="py-0">
              {{ $t("form.publication.visibility") }}
            </h5>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.is_external"
              :label="$t('form.publication.external')"
            />
          </v-col>
          <v-col cols="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.hidden"
              :label="$t('form.hidden')"
            />
          </v-col>
          <v-col cols="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.featured"
              :label="$t('form.featured')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <h5 class="py-0">
              {{ $t('form.publication.minervaSyncStatus') }}
            </h5>
            <p class="caption text--secondary mb-3">
              {{ $t('form.publication.minervaSyncHelp') }}
            </p>
            <v-select
              class="mb-4"
              v-model="publication.minerva_sync_option"
              :items="minervaSyncOptions"
              item-text="text"
              item-value="value"
              :label="$t('form.publication.minervaVisibility')"
              :hint="$t('form.publication.minervaVisibilityHelp')"
              persistent-hint
              outlined
              clearable
            />
            <div class="d-flex flex-column">
              <v-chip
                v-if="publication.minerva_sync_status"
                small
                class="align-self-start mb-1"
                :color="minervaStatusColor(publication.minerva_sync_status)"
                :text-color="minervaStatusTextColor(publication.minerva_sync_status)"
              >
                {{ minervaStatusLabel(publication.minerva_sync_status) }}
              </v-chip>
              <span
                class="caption"
                v-if="minervaStatusDescription(publication.minerva_sync_status, publication)"
              >
                {{ minervaStatusDescription(publication.minerva_sync_status, publication) }}
              </span>
              <v-tooltip
                top
                v-if="availableMinervaUrl(publication)"
                class="mt-2 align-self-start"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :href="availableMinervaUrl(publication)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                {{ $t('form.publication.minerva.summary.available') }}
              </v-tooltip>
              <span
                class="caption mt-1"
                v-if="publication.minerva_sync_status === 'missing-data' && minervaMetadataSummary(publication)"
              >
                {{ minervaMetadataSummary(publication) }}
              </span>
            </div>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.abstract") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="publication.abstract"
              :label="$t('form.abstract')"
              outlined
              :rules="rules.char_max_6500"
              :counter="6500"
            ></v-textarea>
          </v-col>
        </v-row>
        <h5>{{ $t("form.keywords") }}</h5>
        <v-row>
          <v-col cols="12">
            <ArrayFieldForm
              v-model="publication.keywords"
              name-field="keywords"
              :label="$t('form.keyword')"
            />
          </v-col>
        </v-row>

        <h5>{{ $t("form.publication.owner") }}</h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-combobox
              outlined
              v-model="ownerSelected"
              :items="namePersons"
              item-value="id"
              item-text="name"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="8" class="py-0">
            <h5>{{ $t("form.authors_text") }}</h5>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="publication.authors_text"
                  :label="$t('form.authors_text')"
                  required
                  outlined
                  :rules="rules.char_max_400"
                  :counter="400"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="4" class="py-0">
            <h5>{{ $t("form.num_authors") }}</h5>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="publication.num_authors"
                  :label="$t('form.num_authors')"
                  required
                  outlined
                  :rules="rules.numAuthorsRules"
                  type="number"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <h5>{{ $t("form.authorsrel") }}</h5>
        <SelectPeopleComboBox
          :items-selected="publication.authorsrel"
          :text-authors="publication.authors_text"
        />
        <h5>
          {{ $t("form.acknowledgements") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="publication.acknowledgements"
              :label="$t('form.acknowledgements')"
              outlined
              :rules="rules.char_max_3500"
              :counter="3500"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="py-0">
            <h5>
              {{ $t('form.doi') }}
            </h5>
            <v-text-field
              v-model="publication.doi"
              :label="$t('form.doi')"
              required
              outlined
              :rules="rules.doiRules"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="py-0">
            <h5>
              {{ $t('form.isbn') }}
            </h5>
            <v-text-field
              v-model="publication.isbn"
              :label="$t('form.isbn')"
              required
              outlined
              :rules="rules.isbnRules"
            ></v-text-field>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.publication.research_areas") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-select
              :items="researchAreas"
              v-model="publication.research_areas"
              :label="$t('form.publication.research_areas')"
              required
              :item-text="'name.' + $i18n.locale"
              item-value="id"
              outlined
              multiple
              :rules="rules.fieldRequired"
            ></v-select>
          </v-col>
        </v-row>
        <h5 class="mb-2">
          {{ $t("collaborations") }}
        </h5>
        <v-row>
          <v-col cols="12" sm="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.regional"
              :label="$t('form.publication.regional')"
              :true-value="true"
              :false-value="false"
              :indeterminate="publication.regional === ''"
              :rules="rules.checkboxRequired"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.national"
              :label="$t('form.publication.national')"
              :true-value="true"
              :false-value="false"
              :indeterminate="publication.national === ''"
              :rules="rules.checkboxRequired"
            />
          </v-col>
          <v-col cols="12" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.international"
              :label="$t('form.publication.international')"
              :true-value="true"
              :false-value="false"
              :indeterminate="publication.international === ''"
              :rules="rules.checkboxRequired"
            />
          </v-col>
        </v-row>

        <h5>Links</h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.publisher"
              :label="$t('label.links.publisher')"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.institutional_repository"
              :label="$t('label.links.institutional_repository')"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.personal_repository"
              :label="$t('label.links.personal_repository')"
              outlined
            />
          </v-col>
        </v-row>
        <v-row v-if="publication.thesis !== null" class="py-0">
          <v-col cols="12">
            <v-text-field
              v-model="publication.links.teseo"
              :label="$t('label.links.teseo')"
              outlined
            />
          </v-col>
        </v-row>
        <h5 v-if="$route.params.id > -1">
          {{ $t("form.files") }}
        </h5>
        <UploadFiles
          v-if="publication.id !== undefined && $route.params.id > -1"
          object_type="publication"
          :id="this.publication.id"
          :final="true"
        />
        <div v-if="publication.article !== null">
          <v-row>
            <v-col cols="12">
              <h3 class="mt-15 mb-0">
                {{ $t("title.articleData") }}
              </h3>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.article.journal") }}
          </h5>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-autocomplete
                outlined
                v-model="publication.article.journal"
                :label="$t('form.article.journal')"
                :rules="rules.fieldRequired"
                :items="journals"
                item-text="title"
                item-value="id"
              >
                <template v-slot:append-outer>
                  <v-btn
                    icon
                    to="/manage/edit/journals"
                    target="_blank"
                    text
                  >
                    <v-icon
                      color="green"
                      v-tooltip.top-center="$t('info.tooltip.addJournal')"
                    >
                      mdi-new-box
                    </v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.article.publication_status") }}
          </h5>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-select
                outlined
                v-model="publication.article.publication_status"
                :label="$t('form.article.publication_status')"
                :rules="rules.fieldRequired"
                :items="publicationStatus"
                item-value="value"
                item-text="text"
              />
            </v-col>
          </v-row>
          <h5>{{ $t("label.dates") }}</h5>
          <v-row>
            <v-col cols="12" sm="4" class="py-0">
              <v-menu
                v-model="menu_accepted_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.article.accepted_date"
                    :label="$t('form.article.accepted_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                    :rules="rules.fieldRequired"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.article.accepted_date"
                  @input="menu_accepted_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4" class="py-0">
              <v-menu
                v-model="menu_publication_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.article.online_publication_date"
                    :label="$t('form.article.online_publication_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.article.online_publication_date"
                  @input="menu_publication_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4" class="py-0">
              <v-menu
                v-model="menu_final_publication_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.article.final_publication_date"
                    :label="$t('form.article.final_publication_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.article.final_publication_date"
                  @input="menu_final_publication_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.article.journal_location") }}
          </h5>
          <v-row>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.article.journal_location.issue_number"
                :label="$t('form.article.issueNumber')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.article.journal_location.volume"
                :label="$t('form.article.volume')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.article.journal_location.page_number"
                :label="$t('form.article.pageNumber')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.article.journal_location.pages"
                :label="$t('form.article.pages')"
                required
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.article.feder_acknowledgements") }}
          </h5>
          <v-row>
            <v-col class="py-0">
              <v-checkbox
                class="big"
                v-model="publication.article.feder_acknowledgements"
                :label="$t('form.federAcknowledgementsDetail')"
              />
            </v-col>
          </v-row>
        </div>
        <div v-if="publication.bookandchapter !== null">
          <v-row>
            <v-col cols="12">
              <h3 class="mt-15 mb-0">
                {{ $t("title.bookAndChapterData") }}
              </h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0">
              <h5>
                {{ $t("form.title") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-text-field
                      v-model="publication.bookandchapter.title"
                      :label="$t('form.title')"
                      outlined
                      :rules="rules.char_max_250"
                      :counter="250"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <h5>
                {{ $t("form.bookandchapter.type") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-select
                    outlined
                    v-model="publication.bookandchapter.type"
                    :label="$t('form.bookandchapter.type')"
                    :rules="rules.fieldRequired"
                    :items="bookAndChapterTypes"
                    item-value="value"
                    item-text="text"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <h5>
                {{ $t("editorial") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-select
                    outlined
                    v-model="publication.bookandchapter.editorial"
                    :label="$t('editorial')"
                    :rules="rules.fieldRequired"
                    :items="editorials"
                    item-value="id"
                    item-text="name"
                  >
                    <template v-slot:append-outer>
                      <v-btn
                        icon
                        to="/manage/edit/editorials"
                        target="_blank"
                        text
                      >
                        <v-icon
                          color="green"
                          v-tooltip.top-center="$t('info.tooltip.addEditorial')"
                        >
                          mdi-new-box
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <h5>
                {{ $t("form.bookandchapter.num_pages") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-text-field
                    v-model="publication.bookandchapter.num_pages"
                    :label="$t('form.bookandchapter.num_pages')"
                    required
                    outlined
                    :rules="rules.numPagesRules"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <h5>
                {{ $t("form.bookandchapter.publication_date") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-menu
                    v-model="menu_publication_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        outlined
                        v-model="publication.bookandchapter.publication_date"
                        :label="$t('form.bookandchapter.publication_date')"
                        prepend-inner-icon="event"
                        readonly
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="publication.bookandchapter.publication_date"
                      @input="menu_publication_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <h5>{{ $t("form.editors") }}</h5>
          <SelectPeopleList :items-selected="book_editors" />
          <h5>
            {{ $t("form.bookandchapter.editors_text") }}
          </h5>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-text-field
                v-model="publication.bookandchapter.editors_text"
                :label="$t('form.bookandchapter.editors_text')"
                outlined
                :rules="rules.char_max_300"
                :counter="300"
              ></v-text-field>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.bookandchapter.book_location") }}
          </h5>
          <v-row>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.bookandchapter.book_location.chapter"
                :label="$t('form.bookandchapter.chapter')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.bookandchapter.book_location.page_number"
                :label="$t('form.bookandchapter.pageNumber')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.bookandchapter.book_location.page_start"
                :label="$t('form.bookandchapter.pageStart')"
                required
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="py-0">
              <v-text-field
                v-model="publication.bookandchapter.book_location.page_end"
                :label="$t('form.bookandchapter.pageEnd')"
                required
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
        </div>
        <div v-if="publication.communication !== null">
          <h3 class="mt-15 mb-0">
            {{ $t("title.communicationData") }}
          </h3>
          <h5>
            {{ $t("form.communication.congress_edition") }}
          </h5>
          <v-row>
            <v-col cols="12" lg="6" class="py-0">
              <v-select
                outlined
                v-model="publication.communication.congress_edition"
                :label="$t('form.communication.congress_edition')"
                :rules="rules.fieldRequired"
                :items="congressEditions"
                item-value="id"
                item-text="name"
              >
                <template v-slot:append-outer>
                  <v-btn
                    icon
                    to="/manage/edit/congressEdition"
                    target="_blank"
                    text
                  >
                    <v-icon
                      color="green"
                      v-tooltip.top-center="
                        $t('info.tooltip.addCongressEdition')
                      "
                    >
                      mdi-new-box
                    </v-icon>
                  </v-btn>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" lg="6" class="py-0">
              <v-checkbox
                v-model="publication.communication.presented_on_main"
                class="big"
                :label="$t('form.communication.mainConferenceDetail')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-0">
              <h5>
                {{ $t("form.communication.date") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-menu
                    v-model="menu_communication_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        outlined
                        v-model="publication.communication.date"
                        :label="$t('form.communication.date')"
                        prepend-inner-icon="event"
                        readonly
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="publication.communication.date"
                      @input="menu_communication_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6" class="py-0">
              <h5>
                {{ $t("form.communication.type") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-select
                    outlined
                    v-model="publication.communication.type"
                    :label="$t('form.communication.type')"
                    :rules="rules.fieldRequired"
                    :items="communicationTypes"
                    item-value="value"
                    item-text="text"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-0">
              <h5>
                {{ $t("form.communication.lecture_notes") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-text-field
                    v-model="publication.communication.lecture_notes"
                    :label="$t('form.communication.lecture_notes')"
                    :rules="rules.char_max_70"
                    :counter="70"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6" class="py-0">
              <h5>
                {{ $t("form.communication.prizes") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-text-field
                    v-model="publication.communication.prizes"
                    :label="$t('form.communication.prizes')"
                    :rules="rules.char_max_150"
                    :counter="150"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.communication.feder_acknowledgements") }}
          </h5>
          <v-row>
            <v-col class="py-0">
              <v-checkbox
                class="big"
                v-model="publication.communication.feder_acknowledgements"
                :label="$t('form.federAcknowledgementsDetail')"
              />
            </v-col>
          </v-row>
        </div>
        <div v-if="publication.thesis !== null">
          <h3 class="mt-15 mb-0">
            {{ $t("title.thesisData") }}
          </h3>
          <h5>
            {{ $t("form.thesis.doctoral_program") }}
          </h5>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-combobox
                v-model="publication.thesis.doctoral_program"
                :label="$t('form.thesis.doctoral_program')"
                :items="doctoralPrograms"
                required
                outlined
                :rules="rules.char_max_150"
                :counter="150"
              ></v-combobox>
            </v-col>
          </v-row>
          <h5>
            {{ $t("label.dates") }}
          </h5>
          <v-row>
            <v-col cols="6" class="py-0">
              <v-menu
                v-model="menu_start_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.thesis.start_date"
                    :label="$t('form.thesis.start_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                    :rules="rules.fieldRequired"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.thesis.start_date"
                  @input="menu_start_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6" class="py-0">
              <v-menu
                v-model="menu_deposit_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.thesis.deposit_date"
                    :label="$t('form.thesis.deposit_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                    :rules="rules.fieldRequired"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.thesis.deposit_date"
                  @input="menu_deposit_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.thesis.thesisLecture") }}
          </h5>
          <v-row>
            <v-col cols="12" sm="12" md="6" class="py-0">
              <v-text-field
                v-model="publication.thesis.read_center"
                :label="$t('form.thesis.read_center')"
                required
                outlined
                :rules="rules.char_max_100"
                :counter="100"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-0">
              <v-menu
                v-model="menu_lecture_date"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    outlined
                    v-model="publication.thesis.lecture_date"
                    :label="$t('form.thesis.lecture_date')"
                    prepend-inner-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="publication.thesis.lecture_date"
                  @input="menu_lecture_date = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-0">
              <v-combobox
                v-model="publication.thesis.grade"
                :label="$t('form.thesis.grade')"
                :items="gradeOptions"
                required
                outlined
                :rules="rules.char_max_40"
                :counter="40"
              ></v-combobox>
            </v-col>
          </v-row>
          <h5>
            {{ $t("form.thesis.international_phd") }}
          </h5>
          <v-row>
            <v-col>
              <v-checkbox
                class="big"
                v-model="publication.thesis.international_phd"
                :label="$t('form.thesis.internationalPhdDetail')"
              />
            </v-col>
          </v-row>

          <h5>
            {{ $t("form.thesis.directors") }}
          </h5>
          <SelectPeopleComboBox
            :items-selected="publication.thesis.directors"
          />
          <h5>{{ $t("form.thesis.tutor") }}</h5>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-select
                :items="namePersons"
                item-text="name"
                item-value="id"
                v-model="publication.thesis.tutor"
                outlined
                :label="$t('form.thesis.tutor')"
              ></v-select>
            </v-col>
          </v-row>
        </div>
        <v-divider />
        <div class="text-center">
          <v-btn text @click="$router.back()">{{ $t("button.back") }}</v-btn>
          <v-btn color="primary" @click="save">{{ $t("button.save") }}</v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import validationRules from "../../../mixins/validationRules";
import SelectPeopleComboBox from "../../../components/reusables/SelectPeopleComboBox";
import ArrayFieldForm from "../../../components/reusables/ArrayFieldForm";
import UploadFiles from "../../../components/reusables/UploadFiles";
import SelectPeopleList from "../../../components/reusables/SelectPeopleList";
import publication_model from "../../../mixins/model_utils/publication_model";
import SearchScopusForm from "@/components/reusables/SearchScopusForm";
import minervaDisplay from "../../../mixins/minervaDisplay";
export default {
  components: {
    UploadFiles,
    ArrayFieldForm,
    SelectPeopleComboBox,
    SelectPeopleList,
    SearchScopusForm
  },
  mixins: [validationRules, publication_model, minervaDisplay],
  name: "EditPublicationDetail",
  data: () => ({
    menu: false,
    menu_accepted_date: false,
    menu_publication_date: false,
    menu_final_publication_date: false,
    menu_communication_date: false,
    menu_start_date: false,
    menu_deposit_date: false,
    menu_lecture_date: false,
    book_editors: [],
    ownerSelected: null,
    publication: {
      owner: null,
      authorsrel: [],
      is_external: false,
      keywords: [""],
      links: {
        publisher: "",
        institutional_repository: "",
        personal_repository: "",
        teseo: ""
      },
      acknowledgements: "",
      authors_text: "",
      research_areas: [],
      hidden: false,
      featured: false,
      language: "en",
      minerva_sync_option: "restricted",
      minerva_sync_status: "pending-check",
      minerva_sync_metadata: {},
      minerva_last_checked_at: null,
      article: null,
      thesis: null,
      bookandchapter: null,
      communication: null
    }
  }),

  created() {
    this.getNamePersons();
    this.getEditorials();
    this.getPublicationStatus();
    this.getAllResearchAreas();
    this.getCongressEditions();
    this.getCommunicationTypes();
    this.getGradeOptions();
    this.getDoctoralProgramOptions();
    this.getBookAndChapterTypes();
    this.getLanguageOptions();
    this.getMinervaSyncOptions();
    this.getJournals();
    if (this.$route.params.id > -1) {
      this.getPublication();
    } else {
      this.initializeForm();
    }
  },

  computed: {
    processData() {
      let bookandchapter, thesis;
      if (this.publication.bookandchapter === null) {
        bookandchapter = null;
      } else {
        bookandchapter = {
          id: this.publication.bookandchapter.id,
          title: this.publication.bookandchapter.title,
          type: this.publication.bookandchapter.type,
          editorial: this.publication.bookandchapter.editorial,
          num_pages:
            this.publication.bookandchapter.num_pages !== ""
              ? this.publication.bookandchapter.num_pages
              : null,
          editors: this.book_editors
            .filter(item => item.id != null)
            .map(function(item) {
              return item.id;
            }),
          editors_text: this.publication.bookandchapter.editors_text,
          book_location: this.publication.bookandchapter.book_location,
          publication_date: this.publication.bookandchapter.publication_date
        };
      }
      if (this.publication.thesis === null) {
        thesis = null;
      } else {
        thesis = {
          id: this.publication.thesis.id,
          directors: this.publication.thesis.directors
            .filter(item => item.person.id != null)
            .map(function(item) {
              return { person: item.person.id, position: item.position };
            }),
          tutor: this.publication.thesis.tutor,
          doctoral_program: this.publication.thesis.doctoral_program,
          start_date: this.publication.thesis.start_date,
          deposit_date: this.publication.thesis.deposit_date,
          lecture_date: this.publication.thesis.lecture_date,
          read_center: this.publication.thesis.read_center,
          grade: this.publication.thesis.grade,
          international_phd: this.publication.thesis.international_phd
        };
      }
      let data = {
        title: this.publication.title,
        abstract: this.publication.abstract,
        acknowledgements: this.publication.acknowledgements,
        keywords: this.publication.keywords,
        links: this.publication.links,
        owner: this.ownerSelected != null ? this.ownerSelected.id : null,
        authorsrel: this.publication.authorsrel
          .filter(item => item.person.id != null)
          .map(function(item) {
            return { person: item.person.id, position: item.position };
          }),
        authors_text: this.publication.authors_text,
        num_authors: this.publication.num_authors,
        isbn: this.publication.isbn,
        doi: this.publication.doi,
        research_areas: this.publication.research_areas.map(a => typeof a == "object" ? a.id : a).filter(area =>
          Number.isInteger(area)
        ),
        language: this.publication.language,
        minerva_sync_option: this.publication.minerva_sync_option,
        regional: this.publication.regional,
        national: this.publication.national,
        international: this.publication.international,
        is_external: this.publication.is_external,
        hidden: this.publication.hidden,
        featured: this.publication.featured,
        article: this.publication.article,
        thesis: thesis,
        bookandchapter: bookandchapter,
        communication: this.publication.communication
      };
      return data;
    }
  },

  methods: {
    ensurePublicationDefaults() {
      if (!this.publication.language) {
        this.$set(this.publication, "language", "en");
      }
      if (this.publication.minerva_sync_option === undefined) {
        this.$set(this.publication, "minerva_sync_option", "restricted");
      }
      if (!this.publication.minerva_sync_status) {
        this.$set(this.publication, "minerva_sync_status", "pending-check");
      }
      if (!this.publication.minerva_sync_metadata || typeof this.publication.minerva_sync_metadata !== "object") {
        this.$set(this.publication, "minerva_sync_metadata", {});
      }
      if (this.publication.minerva_last_checked_at === undefined) {
        this.$set(this.publication, "minerva_last_checked_at", null);
      }
    },
    initializeForm() {
      switch (this.$route.params.type) {
        case "article":
          this.publication.article = {
            publication: "",
            journal: "",
            accepted_date: null,
            online_publication_date: null,
            final_publication_date: null,
            num_pages: null,
            publication_status: "",
            journal_location: {
              issue_number: "",
              volume: "",
              page_number: "",
              pages: ""
            },
            feder_acknowledgements: false
          };
          break;
        case "bookandchapters":
          this.publication.bookandchapter = {
            "title": "",
            type: "",
            editorial: "",
            num_pages: null,
            editors: [],
            editors_text: "",
            publication_date: "",
            book_location: {
              chapter: "",
              page_number: "",
              page_start: "",
              page_end: ""
            }
          };
          break;
        case "communications":
          this.publication.communication = {
            congress_edition: "",
            type: "",
            date: null,
            lecture_notes: "",
            prizes: "",
            presented_on_main: false,
            feder_acknowledgements: false
          };
          break;
        case "thesis":
          this.publication.thesis = {
            doctoral_program: "",
            start_date: "",
            deposit_date: "",
            lecture_date: "",
            read_center: "",
            grade: "",
            international_phd: false,
            tutor: "",
            directors: []
          };
      }
      this.ensurePublicationDefaults();
    },

    getPublication() {
      let path =
        this.$store.state.endpoint + "/publication/" + this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.publication = response.data;
          this.ensurePublicationDefaults();
          this.namePersons.forEach(person => {
            if (person.id === this.publication.owner) {
              this.ownerSelected = person;
            }
          });
          this.getPersonal();
        })
        .catch(error => {
          console.log(error);
        });
    },
    getPersonal() {
      const path = this.$store.state.endpoint + "/personName";
      axios
        .get(path)
        .then(response => {
          if (this.publication.bookandchapter != null) {
            this.publication.bookandchapter.editors.forEach(id => {
              const item = response.data.find(
                itemResponse => itemResponse.id === id
              );
              if (item !== undefined) {
                this.book_editors.push({
                  id: item.id,
                  name: item.preferred_name.given_name
                });
              }
            });
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    checkPositionPersons() {
      if (this.publication.authorsrel.length) {
        const positions = this.publication.authorsrel.map(function(item) {
          return item.position.toString();
        });
        let findDuplicates = arr =>
          arr.filter((item, index) => arr.indexOf(item) !== index);

        if (findDuplicates(positions).length <= 0) {
          return true;
        } else {
          this.$store.commit("alert", {
            color: "error",
            text: this.$t("form.validation.positions")
          });
          return false;
        }
      } else {
        this.$store.commit("alert", {
          color: "error",
          text: this.$t("form.validation.authorsRequired")
        });
        return false;
      }
    },

    save() {
      if (this.$refs.form.validate() && this.checkPositionPersons()) {
        let path;
        if (this.$route.params.id > -1) {
          path =
            this.$store.state.endpoint +
            "/publication/" +
            this.$route.params.id;
          axios
            .patch(path, this.processData)
            .then(response => {
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.updateItem")
              });
              this.$router.push("/manage/edit/publications");
            })
            .catch(error => {
              console.log(error);
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        } else {
          path = this.$store.state.endpoint + "/publication";
          axios
            .post(path, this.processData)
            .then(response => {
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.createItem")
              });
              this.$router.push("/manage/edit/publications");
            })
            .catch(error => {
              console.log(error);
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        }
      }
    },
    loadJournalSelected(item) {
      if (item != undefined) {
        if (typeof item == "number") {
          this.journalSelected = this.journals.filter(
            journal => journal.id === item
          )[0];
        } else {
          this.journalSelected = item;
        }
      }
    },
    loadCongressEditionSelected(item) {
      if (item != undefined) {
        if (typeof item == "number") {
          this.congressEditionSelected = this.congressEditions.filter(
            congressEdition => congressEdition.id === item
          )[0];
        } else {
          this.congressEditionSelected = item;
        }
      }
    },
    handleScopusData(data) {
    console.log(this.publication);
      this.publication.abstract = data.abstract;
      this.publication.keywords = data.keywords;
      this.publication.authors_text = data.authors_text;
      this.publication.num_authors = data.num_authors;
      this.publication.doi = data.doi;
      if(this.publication.article !== null){
        this.publication.article.final_publication_date = data.publication_date;
        this.publication.article.journal = { id: null, title: data.journal };
        this.loadJournalSelected({ id: null, title: data.journal });
      }
      if(this.publication.communication !== null){
        this.publication.communication.date = data.publication_date;
        this.publication.communication.congress_edition = { id: null, name: data.conference };
        this.loadCongressEditionSelected({ id: null, name: data.conference });
      }
      this.publication.journal_location = {volume: data.volume, pages: data.page_range };
      this.publication.links.publisher = data.url;
      this.publication.title = data.title;
    }
  }
};
</script>

<style scoped></style>
